import { useState, useReducer } from 'react';
import { addCompetitors } from '@hydra/utils/settingsApi';

function urlHasTLD (url) {
  return url.split('.').length > 1;
}

function ensureProtocol (url) {
  if (/^https?:\/\//i.test(url)) {
    return url;
  } else if (/^\/\//.test(url)) {
    return `http:${url}`;
  } else {
    return `http://${url}`;
  }
}

function parseAbsoluteUrl (url) {
  try {
    return (new URL(ensureProtocol(url.trim()))).href;
  } catch (error) {
    return url;
  }
}

const initialState = (competitors) => ({
  comp1: {
    value: competitors[0] ? competitors[0].url : '',
    name: 'comp1',
    label: 'competitor_1_label',
    placeholder: 'competitor_1_placeholder',
    valid: true,
    errorMessage: ''
  },
  comp2: {
    value: competitors[1] ? competitors[1].url : '',
    name: 'comp2',
    label: 'competitor_2_label',
    placeholder: 'competitor_2_placeholder',
    valid: true,
    errorMessage: ''
  },
  comp3: {
    value: competitors[2] ? competitors[2].url : '',
    name: 'comp3',
    label: 'competitor_3_label',
    placeholder: 'competitor_3_placeholder',
    valid: true,
    errorMessage: ''
  }
});

const reducer = (state, action) => {
  switch (action.action) {
    case 'setValue':
      return {
        ...state,
        [action.competitor]: {
          ...state[action.competitor],
          value: action.value,
          errorMessage: ''
        }
      };
    case 'setValid':
      return {
        ...state,
        [action.competitor]: {
          ...state[action.competitor],
          valid: action.valid,
          errorMessage: action.errorMessage
        }
      };
    case 'setClear':
      return {
        ...state,
        [action.competitor]: {
          ...state[action.competitor],
          value: action.value,
          valid: action.valid,
          errorMessage: ''
        }
      };
  }
};

const useCompetitors = ({ projectUrl, competitors }) => {
  const [hasDuplicates, setHasDuplicates] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [state, dispatch] = useReducer(reducer, initialState(competitors));

  const handleAddCompetitors = async e => {
    const competitorsToSubmit = Object.values(state).reduce(
      (competitors, { value }) => {
        if (value.length > 0) {
          competitors.push(value);
        }
        return competitors;
      },
      []
    );
    const response = await addCompetitors(competitorsToSubmit, projectUrl);
    return checkResolvable(response);
  };

  const checkResolvable = (response) => {
    let valid = true;
    if (response.code === 'INVALID_COMPETITORS') {
      valid = false;
      const { resolves } = response;
      const invalidUrls = resolves.reduce(
        (invalidUrls, resolveResponse) => {
          const { resolvable, url } = resolveResponse;
          if (!resolvable) {
            invalidUrls.push(url);
          }
          return invalidUrls;
        },
        []
      );
      Object.keys(state).forEach(key => {
        const value = state[key].value;
        const name = state[key].name;
        if (value && invalidUrls.includes(parseAbsoluteUrl(value))) {
          dispatch({
            action: 'setValid',
            competitor: name,
            valid: false,
            errorMessage: urlHasTLD(value)
              ? 'generation_form_competitor_unresolvable_url'
              : 'generation_form_competitor_invalid_url'
          });
        }
      });
    }
    return valid;
  };

  const checkValidity = () => {
    let valid = true;
    if (!validateUrls()) {
      valid = false;
    }
    if (containsDuplicates([state.comp1.value, state.comp2.value, state.comp3.value])) {
      valid = false;
    }
    return valid;
  };

  const validateUrls = () => {
    let valid = true;
    const parseUrl = url => new URL(/^https?:\/\//i.test(url) ? url : `https://${url}`);
    Object.keys(state).forEach(key => {
      const url = state[key].value;
      if (url === '') {
        return valid;
      }
      const name = state[key].name;
      const parsed = parseUrl(url);
      if (parsed.pathname !== '/' || parsed.search) {
        valid = false;
        dispatch({ action: 'setValid', competitor: name, valid: false });
      } else {
        dispatch({ action: 'setValid', competitor: name, valid: true });
      }
    });
    return valid;
  };

  const containsDuplicates = (competitors) => {
    const hasDuplicate = competitors.some((val, i) => competitors.indexOf(val) !== i && val.length);
    setHasDuplicates(hasDuplicate);
    return hasDuplicate;
  };

  const handleChange = e => {
    if (e.target.value === '') {
      return dispatch({ action: 'setClear', competitor: e.target.name, value: '', valid: true });
    }
    dispatch({ action: 'setValue', competitor: e.target.name, value: e.target.value });
  };

  const handleClear = name => {
    dispatch({ action: 'setClear', competitor: name, value: '', valid: true });
  };

  const clearErrors = () => {
    Object.keys(state).forEach(key => {
      const name = state[key].name;
      dispatch({ action: 'setValid', competitor: name, valid: true });
    });
  };

  const handleSaveSuccess = (state) => setSaveSuccess(state);

  return {
    handleAddCompetitors,
    checkValidity,
    handleChange,
    handleClear,
    clearErrors,
    saveSuccess,
    handleSaveSuccess,
    hasDuplicates,
    state,
    containsDuplicates
  };
};

export default useCompetitors;
